'use client';

import { format } from 'date-fns/format';
import * as React from 'react';
import { type DateRange } from 'react-day-picker';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/calendar';
import {
  PopoverRoot,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover/popover';
import { cn } from '@/lib/utils';

interface DatePickerContextValue {
  date?: DateRange;
  onDateChange?: (date: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

const DatePickerContext = React.createContext<DatePickerContextValue | null>(
  null
);

const useDatePickerContext = () => {
  const context = React.useContext(DatePickerContext);
  if (!context) {
    throw new Error(
      'DatePicker components must be used within DatePicker.Root'
    );
  }
  return context;
};

interface DatePickerRootProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange;
  onDateChange?: (date: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

const DatePickerRoot = React.forwardRef<HTMLDivElement, DatePickerRootProps>(
  (
    {
      className,
      date,
      onDateChange,
      placeholder = 'Pick a date range',
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const [internalDate, setInternalDate] = React.useState<
      DateRange | undefined
    >();

    const currentDate = date !== undefined ? date : internalDate;
    const handleDateChange = onDateChange || setInternalDate;

    const contextValue: DatePickerContextValue = {
      date: currentDate,
      onDateChange: handleDateChange,
      placeholder,
      disabled,
    };

    return (
      <DatePickerContext.Provider value={contextValue}>
        <div ref={ref} className={cn('grid gap-2', className)} {...props}>
          <PopoverRoot>{children}</PopoverRoot>
        </div>
      </DatePickerContext.Provider>
    );
  }
);
DatePickerRoot.displayName = 'DatePickerRoot';

interface DatePickerTriggerProps extends React.ComponentProps<typeof Button> {
  showIcon?: boolean;
}

const DatePickerTrigger = React.forwardRef<
  HTMLButtonElement,
  DatePickerTriggerProps
>(({ className, showIcon = true, children, ...props }, ref) => {
  const { date, placeholder, disabled } = useDatePickerContext();

  if (children) {
    return (
      <PopoverTrigger asChild ref={ref} {...props}>
        {children}
      </PopoverTrigger>
    );
  }

  return (
    <PopoverTrigger asChild>
      <Button
        ref={ref}
        variant="outline"
        disabled={disabled}
        className={cn(
          'w-[300px] justify-start text-left font-normal',
          !date && 'text-muted-foreground',
          className
        )}
        {...props}
      >
        {showIcon && <Icons.calendar className="mr-2 h-4 w-4" />}
        {date?.from ? (
          date.to ? (
            <>
              {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
            </>
          ) : (
            format(date.from, 'LLL dd, y')
          )
        ) : (
          <span>{placeholder}</span>
        )}
      </Button>
    </PopoverTrigger>
  );
});
DatePickerTrigger.displayName = 'DatePickerTrigger';

interface DatePickerContentProps extends React.ComponentProps<
  typeof PopoverContent
> {
  numberOfMonths?: number;
}

const DatePickerContent = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  DatePickerContentProps
>(({ className, numberOfMonths = 2, children, ...props }, ref) => {
  const { date, onDateChange } = useDatePickerContext();

  return (
    <PopoverContent
      ref={ref}
      className={cn('w-auto py-1', className)}
      align="start"
      {...props}
    >
      <Calendar
        autoFocus
        mode="range"
        defaultMonth={date?.from}
        selected={date}
        onSelect={onDateChange}
        numberOfMonths={numberOfMonths}
      />
      {children}
    </PopoverContent>
  );
});
DatePickerContent.displayName = 'DatePickerContent';

interface DatePickerResetProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
}

const DatePickerReset = React.forwardRef<
  HTMLButtonElement,
  DatePickerResetProps
>(({ className, children, onClick, ...props }, ref) => {
  const { date, onDateChange } = useDatePickerContext();

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    onDateChange?.(undefined);
    onClick?.(e);
  };

  if (!date) {
    return null;
  }

  return (
    <div className="pt-1 border-t border-border">
      <Button
        ref={ref}
        variant="ghost"
        size="sm"
        onClick={handleReset}
        className={cn('w-full gap-2', className)}
        {...props}
      >
        {children || (
          <>
            <Icons.cancel className="size-3" />
            Clear dates
          </>
        )}
      </Button>
    </div>
  );
});
DatePickerReset.displayName = 'DatePickerReset';

export const DatePicker = {
  Root: DatePickerRoot,
  Trigger: DatePickerTrigger,
  Content: DatePickerContent,
  Reset: DatePickerReset,
};
